"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePauseResumeCheckInfo = void 0;
const react_1 = require("react");
const lodash_1 = require("lodash");
const interfaces_1 = require("../interfaces");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const apis_1 = require("../apis");
const TradeAPILib_1 = require("TradeAPILib");
const trade_utils_lib_1 = require("trade_utils_lib");
const parseParamsToRequest = (_params) => {
    if (!_params.open_price || !Number(_params.open_price)) {
        throw { code: -1, message: "no open price" };
    }
    const { exchangeOrder } = _params;
    const isPaused = exchangeOrder.innerStatus === TradeAPILib_1.OrderInnerStatus.paused;
    if (_params.type === interfaces_1.PauseResumeType.pause && isPaused) {
        throw { code: -100, message: "Local: order status invalid" };
    }
    if (_params.type === interfaces_1.PauseResumeType.resume && !isPaused) {
        throw { code: -101, message: "Local: order status invalid" };
    }
    const req = {
        orderId: _params.orderId,
        type: _params.type,
        open_price: _params.open_price,
        trigger_pause_price_up: _params.trigger_pause_price_up,
        trigger_pause_price_down: _params.trigger_pause_price_down,
    };
    return req;
};
const queryAPI = (request) => {
    return (0, apis_1.pauseResumeCheckAPI)(request);
};
const usePauseResumeCheckInfo = (params, $st) => {
    const [checkInfo, setCheckInfo] = (0, react_1.useState)();
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)();
    const { open_price, orderId, trigger_pause_price_down, trigger_pause_price_up, type, exchangeOrder } = params;
    const depsExPrice = [type, orderId, trigger_pause_price_down, trigger_pause_price_up];
    const requestRef = (0, react_1.useRef)(params);
    requestRef.current = params;
    const debounceFn = (0, react_1.useRef)((0, lodash_1.debounce)(() => {
        const sub = (0, rxjs_1.of)(requestRef.current)
            .pipe((0, operators_1.map)((reqOp) => {
            return parseParamsToRequest(reqOp);
        }), (0, operators_1.mergeMap)((req) => {
            setLoading(true);
            return queryAPI(req);
        }))
            .subscribe({
            next: (data) => {
                setError(undefined);
                setCheckInfo(data);
                setLoading(false);
            },
            error: (error) => {
                setError(error);
                setCheckInfo(undefined);
                setLoading(false);
                console.log(`usePauseResumeCheckInfo debounce: ${JSON.stringify(error)}`);
            },
        });
        return sub;
    }, 500));
    // 用户输入防抖
    (0, react_1.useEffect)(() => {
        var _a;
        const sub = (_a = debounceFn.current) === null || _a === void 0 ? void 0 : _a.call(debounceFn);
        return () => sub === null || sub === void 0 ? void 0 : sub.unsubscribe();
    }, depsExPrice);
    const throttleDepensOnPriceFn = (0, react_1.useRef)((0, lodash_1.throttle)(() => {
        const sub = (0, rxjs_1.of)(requestRef.current)
            .pipe((0, operators_1.map)((reqOp) => {
            return parseParamsToRequest(reqOp);
        }), (0, operators_1.mergeMap)((req) => {
            return queryAPI(req);
        }))
            .subscribe({
            next: (data) => {
                setCheckInfo(data);
            },
            error: (error) => {
                console.log(`usePauseResumeCheckInfo throttle: ${JSON.stringify(error)}`);
            },
        });
        return sub;
    }, 5000));
    // 当前价格变更节流
    (0, react_1.useEffect)(() => {
        var _a;
        // 只有在当前参数合理时，才去根据价格刷新数据
        if (!checkInfo || !checkInfo.check_result) {
            return;
        }
        const sub = (_a = throttleDepensOnPriceFn.current) === null || _a === void 0 ? void 0 : _a.call(throttleDepensOnPriceFn);
        return () => sub === null || sub === void 0 ? void 0 : sub.unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open_price]);
    const queryCheckInfo = (0, react_1.useCallback)((investParam) => {
        if (investParam) {
            const p = Object.assign(Object.assign({}, requestRef.current), { quote_investment: investParam });
            requestRef.current = p;
        }
        const request = parseParamsToRequest(requestRef.current);
        return queryAPI(request).pipe((0, operators_1.tap)((value) => {
            setCheckInfo(value);
        }));
    }, []);
    // 强平价不利
    const liquidPriceAdversely = (0, react_1.useMemo)(() => {
        if ((0, lodash_1.isUndefined)(checkInfo === null || checkInfo === void 0 ? void 0 : checkInfo.check_result) || !exchangeOrder || !$st)
            return null;
        const innerOrder = exchangeOrder.exchangeGridV4FuturesOrder;
        const top = innerOrder.high;
        const bottom = innerOrder.low;
        const isNoTrend = innerOrder.trend === TradeAPILib_1.GridFuturesTrend.no_trend;
        const isLong = innerOrder.trend === TradeAPILib_1.GridFuturesTrend.long;
        const isShort = innerOrder.trend === TradeAPILib_1.GridFuturesTrend.short;
        const upAfter = checkInfo === null || checkInfo === void 0 ? void 0 : checkInfo.estimate_liquidation_price_up_after;
        const downAfter = checkInfo === null || checkInfo === void 0 ? void 0 : checkInfo.estimate_liquidation_price_down_after;
        // 强平价不利于现价
        const liqPriceCurrentPriceCheck = () => {
            const curPrice = params.open_price;
            if (!curPrice)
                return null;
            const isPriceGreatThenUp = new trade_utils_lib_1.SafeDecimal(upAfter).greaterThan(0) && new trade_utils_lib_1.SafeDecimal(curPrice).gte(new trade_utils_lib_1.SafeDecimal(upAfter));
            const isPriceLessThenDown = new trade_utils_lib_1.SafeDecimal(downAfter).greaterThan(0) && new trade_utils_lib_1.SafeDecimal(curPrice).lte(new trade_utils_lib_1.SafeDecimal(downAfter));
            if ((isNoTrend && (isPriceGreatThenUp || isPriceLessThenDown)) ||
                (isLong && isPriceLessThenDown) ||
                (isShort && isPriceGreatThenUp)) {
                return $st("grid_futures_resume_liq_warning_current_price_msg_after");
            }
            return null;
        };
        // 强平价格在区间内
        const liqPriceRangeCheck = () => {
            const isInPriceRange = (v) => {
                if (!top || !bottom || !v)
                    return false;
                return new trade_utils_lib_1.SafeDecimal(v).lte(new trade_utils_lib_1.SafeDecimal(top)) && new trade_utils_lib_1.SafeDecimal(v).gte(new trade_utils_lib_1.SafeDecimal(bottom));
            };
            const isUpInPriceRange = isInPriceRange(upAfter);
            const isDownInPriceRange = isInPriceRange(downAfter);
            if ((isNoTrend && (isUpInPriceRange || isDownInPriceRange)) ||
                (isLong && isDownInPriceRange) ||
                (isShort && isUpInPriceRange)) {
                return $st("grid_futures_resume_liq_warning_range_msg_after");
            }
            return null;
        };
        return liqPriceCurrentPriceCheck() || liqPriceRangeCheck();
    }, [
        checkInfo === null || checkInfo === void 0 ? void 0 : checkInfo.check_result,
        checkInfo === null || checkInfo === void 0 ? void 0 : checkInfo.estimate_liquidation_price_up_after,
        checkInfo === null || checkInfo === void 0 ? void 0 : checkInfo.estimate_liquidation_price_down_after,
        exchangeOrder,
        $st,
        params.open_price,
    ]);
    return { checkInfo, queryCheckInfo, loading: loading, liquidPriceAdversely, error };
};
exports.usePauseResumeCheckInfo = usePauseResumeCheckInfo;
